import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import Link from '@mui/material/Link'
import { useTranslation } from 'react-i18next'
import { SelectChangeEvent } from '@mui/material/Select'
import { IContactOrder } from '~/src/repositories/Container/schema/Container'
import { convertSupplyType, ISupplyType } from '~/src/utils/convert'
import { formatDate } from '~/src/utils/formatDate'
import { formatMoney } from '~/src/utils/formatMoney'
import { StatusType } from '~/src/constants/requestOrder'
import { orderRepository } from '~/src/repositories'
import { useMessage, useCurrency } from '~/src/hooks'
import { stringToNumber } from '~/src/libs'

interface ITableContactOrderItem {
  id: number
  refNo: string
  name: string
  status: number
  remark: string
  maker: string
  carName: string
  modelCode: string
  engineModel: string
  year: string
  type: string
  price: string
  transmission: string
  productName: string
  date: number
  supplyType: ISupplyType
  user: {
    companyName: string
    country: string
    email: string
    id: number
    name: string
  }
}
interface IColumn {
  id:
    | 'checked'
    | 'id'
    | 'refNo'
    | 'companyName'
    | 'name'
    | 'status'
    | 'remark'
    | 'maker'
    | 'carName'
    | 'modelCode'
    | 'engineModel'
    | 'year'
    | 'type'
    | 'date'
    | 'price'
    | 'transmission'
    | 'productName'
    | 'date'
    | 'supplyType'
  label: string | React.ReactNode
  minWidth?: number
  align?: 'right'
  render: (item: ITableContactOrderItem, index: number) => React.ReactNode
}

interface ITableContactOrderProps {
  data?: IContactOrder['data']
}

export const TableContactOrderComponents: React.FC<ITableContactOrderProps> = ({
  data,
}): JSX.Element => {
  const { t } = useTranslation()
  const [selected, setSelected] = React.useState<string[]>([])
  const [page, setPage] = useState<number>(0)

  const [filter, setFilter] = React.useState<string>('')
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [status, setStatus] = useState('')
  const { handleOpenWithErrorMessage, handleOpenWithSuccessMessage } = useMessage()
  const { baseCurrencyUnit, unitPosition } = useCurrency()

  const isSelected = (name: string) => selected.indexOf(name) !== -1

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && data) {
      const newSelecteds = data?.items.map((n) => n.id.toString())
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  /**
   * handleChangeStatus
   * @param e
   */
  const handleChangeStatus = (e: SelectChangeEvent) => {
    setStatus(e.target.value)
  }
  /**
   * getKeyByValue
   * @param object
   * @param value
   */
  const getKeyByValue = (object: Record<string, unknown>, value: unknown) => {
    return Object.keys(object).find((key) => object[key] === value)
  }
  /**
   * handleBatchOperation
   */
  const handleBatchOperation = async () => {
    try {
      const res = await Promise.all(
        selected.map(async (id) => await orderRepository.patchRequestOrder(id, { status }))
      )
      console.log(res)
      handleOpenWithSuccessMessage(t('container_order_detail.updated'))
    } catch (e: any) {
      console.error(e)
      handleOpenWithErrorMessage(e.message)
    } finally {
      window.location.reload()
    }
  }

  const handleClick = (event: React.ChangeEvent<HTMLInputElement>, name: string): void => {
    const selectedIndex = selected.indexOf(name)
    let newSelected: string[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }
    setSelected(newSelected)
  }
  const columns: IColumn[] = [
    {
      id: 'checked',
      // label: `${t('container_order_detail.ref_no.')}`,
      label: (
        <Checkbox
          size={'small'}
          color="primary"
          indeterminate={selected.length > 0 && selected.length < (data?.items.length ?? 0)}
          checked={(data?.items.length ?? 0) > 0 && selected.length === data?.items.length}
          onChange={handleSelectAllClick}
          inputProps={{
            'aria-label': 'select all desserts',
          }}
        />
      ),
      minWidth: 75,
      render: ({ id }, index) => (
        <Checkbox
          size={'small'}
          onChange={(event) => handleClick(event, id.toString())}
          color="primary"
          checked={isSelected(id.toString())}
          inputProps={{
            'aria-labelledby': `table-contact-order-checkbox-${index}`,
          }}
        />
      ),
    },
    {
      id: 'id',
      label: `${t('container_order_detail.remarks_contact_no')}`,
      minWidth: 150,
      render: ({ id }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {id}
        </Typography>
      ),
    },
    {
      id: 'refNo',
      label: `${t('container_order_detail.ref_no.')}`,
      minWidth: 100,
      render: ({ refNo }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {refNo}
        </Typography>
      ),
    },
    {
      id: 'companyName',
      label: `${t('container_order_detail.company_name')}`,
      minWidth: 100,
      render: ({ user }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
          }}
        >
          {user.companyName}
        </Typography>
      ),
    },
    {
      id: 'productName',
      label: `${t('container_order_detail.product_name')}`,
      minWidth: 100,
      render: ({ productName }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
          }}
        >
          {productName}
        </Typography>
      ),
    },
    {
      id: 'name',
      label: `${t('container_order_detail.name')}`,
      minWidth: 400,
      render: ({ id, name }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
          }}
        >
          <Link
            href={`/admin/order/contact-order/detail/${id}`}
            underline="hover"
            target="_blank"
            rel="noreferrer"
          >
            {name}
          </Link>
        </Typography>
      ),
    },
    {
      id: 'status',
      label: `${t('container_order_detail.status')}`,
      minWidth: 100,
      render: ({ status }) => {
        return status === 0 ? (
          <Chip
            label={status}
            variant="outlined"
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
              height: '24px',
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          >
            {status}
          </Typography>
        )
      },
    },
    {
      // 調達区分
      id: 'supplyType',
      label: `${t('container_order_detail.supply_type')}`,
      minWidth: 120,
      render: ({ supplyType }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {convertSupplyType(supplyType)}
        </Typography>
      ),
    },
    {
      id: 'price',
      label: `${t('container_order_detail.price')}`,
      minWidth: 120,
      render: ({ price }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatMoney(stringToNumber(price), baseCurrencyUnit, unitPosition)}
        </Typography>
      ),
    },
    {
      id: 'date',
      label: `${t('container_order_detail.data_update_date')}`,
      minWidth: 150,
      render: ({ date }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatDate(date)}
        </Typography>
      ),
    },
  ]

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const handleChange = (event: SelectChangeEvent): void => {
    setFilter(event.target.value)
  }
  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <Box>
        <Box sx={{ padding: '16px', boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.12)' }}>
          <Typography
            variant={'h6'}
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 500,
              fontSize: '20px',
              lineHeight: '32px',
              letterSpacing: '0.15px',
              color: '#000000',
            }}
          >
            {t('container_order_detail.contact_order')}
          </Typography>
        </Box>

        <Stack direction={'row'} spacing={1} padding={'20px'}>
          <Select
            value={status}
            displayEmpty
            renderValue={(value) =>
              getKeyByValue(StatusType, Number(value)) || t('contact_order.bulk_operation')
            }
            inputProps={{ 'aria-label': 'Without label' }}
            size={'small'}
            sx={{
              opacity: !status ? '0.6' : '1',
              width: '220px',
              fontSize: '16px',
              lineHeight: '24px',
              fontStyle: 'normal',
              background: 'white',
              height: '40px',
            }}
            onChange={handleChangeStatus}
          >
            <MenuItem value={'100'}>PENDING</MenuItem>
            <MenuItem value={'300'}>NEGOTIATION</MenuItem>
            <MenuItem value={'400'}>CANCEL</MenuItem>
            <MenuItem value={'500'}>ACCEPTED</MenuItem>
            <MenuItem value={'600'}>IN PROGRESS</MenuItem>
            <MenuItem value={'1000'}>CLOSED</MenuItem>
          </Select>
          <Button
            variant="contained"
            size={'small'}
            onClick={handleBatchOperation}
            disabled={!status || !selected.length}
          >
            <Typography
              sx={{
                fontStyle: 'normal',
                fontSize: 14,
                lineHeight: '24px',
                fontWeight: 500,
              }}
            >
              {t('contact_order.be_utilized')}
            </Typography>
          </Button>
        </Stack>
      </Box>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth, background: '#FFFFFF' }}
                >
                  <Typography
                    sx={{
                      borderRight: 2,
                      borderColor: `${index === columns.length - 1 ? 'white' : '#E0E0E0'}`,
                      fontSize: 14,
                      lineHeight: '24px',
                      fontStyle: 'normal',
                      fontWeight: 'medium',
                    }}
                  >
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.items
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item, index) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    {columns.map((column, i) => {
                      return (
                        <TableCell key={i} align={column.align}>
                          {column.render(item, index)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={data ? data?.pagination.totalCount : 0}
        rowsPerPage={rowsPerPage}
        sx={{
          fontSize: '12px',
          fontStyle: 'normal',
          lineHeight: '20px',
          fontWeight: 'Regular',
        }}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  )
}
